/** Scompone un indirizzo italiano scritto su una riga (letto da documenti) in via, civico, comune, provincia e CAP. */
export interface ItalianAddress { via: string; civico: string; comune: string; provincia: string; cap: string }

const PROVINCE = new Set(('AG AL AN AO AP AQ AR AT AV BA BG BI BL BN BO BR BS BT BZ CA CB CE CH CL CN CO CR CS CT CZ ' +
  'EN FC FE FG FI FM FR GE GO GR IM IS KR LC LE LI LO LT LU MB MC ME MI MN MO MS MT NA NO NU OR PA PC PD PE PG PI PN PO ' +
  'PR PT PU PV PZ RA RC RE RG RI RM RN RO SA SI SO SP SR SS SU SV TA TE TN TO TP TR TS TV UD VA VB VC VE VI VR VT VV').split(' '))

const STREET = /^(via|viale|v\.le|piazza|p\.zza|p\.za|piazzale|piazzetta|corso|c\.so|largo|vicolo|vico|contrada|c\.da|localit[aà]|loc\.|strada|str\.|borgo|frazione|fraz\.|lungomare|salita|traversa|rione|circonvallazione|galleria|regione|quartiere|parco|s\.s\.|s\.p\.)\b/i
const CIVIC = String.raw`(?:n\.?|n°|nr\.?|civ\.?)?\s*(\d+\s*[a-z]?(?:\s*\/\s*[a-z0-9]+)?|snc|s\.n\.c\.?)`
const CIVIC_ONLY = new RegExp(`^${CIVIC}$`, 'i')
const CIVIC_END = new RegExp(`^(.*?)[\\s,]+${CIVIC}$`, 'i')

const clean = (s: string) => s.replace(/\s+/g, ' ').replace(/^[\s,;\-]+|[\s,;\-]+$/g, '')

function splitCivic(street: string): { via: string; civico: string } {
  const m = CIVIC_END.exec(street)
  return m && clean(m[1]) ? { via: clean(m[1]), civico: m[2].replace(/\s+/g, '').toUpperCase() } : { via: street, civico: '' }
}

export function parseItalianAddress(raw: string | null | undefined): ItalianAddress {
  const out: ItalianAddress = { via: '', civico: '', comune: '', provincia: '', cap: '' }
  let s = clean(String(raw ?? '')).replace(/[,\s-]+(italia|italy)$/i, '')
  if (!s) return out

  // CAP: l'ultimo gruppo di 5 cifre, con o senza "CAP".
  const caps = [...s.matchAll(/(?:\bC\.?A\.?P\.?:?\s*)?\b(\d{5})\b/gi)]
  const capM = caps[caps.length - 1]
  if (capM) { out.cap = capM[1]; s = s.slice(0, capM.index) + ' | ' + s.slice(capM.index! + capM[0].length) }

  // Provincia: "(MI)" oppure sigla finale valida.
  const p = /\(\s*([a-z]{2})\s*\)/i.exec(s)
  if (p && PROVINCE.has(p[1].toUpperCase())) {
    out.provincia = p[1].toUpperCase(); s = s.slice(0, p.index) + ' | ' + s.slice(p.index + p[0].length)
  } else {
    const t = /[\s,\-|]+([a-z]{2})\.?$/i.exec(s)
    if (t && PROVINCE.has(t[1].toUpperCase()) && clean(s.slice(0, t.index))) { out.provincia = t[1].toUpperCase(); s = s.slice(0, t.index) + ' |' }
  }

  const parts = s.split(/\||,|;|\s[-–]\s/).map(clean).filter(Boolean)
  if (!parts.length) return out
  const foundPlace = !!(out.cap || out.provincia)

  let idx = parts.findIndex(x => STREET.test(x))
  if (idx < 0) idx = 0
  let street = parts[idx]
  const rest = parts.filter((_, i) => i !== idx)
  // Civico in un segmento a sé ("Via Roma, 12").
  const civIdx = rest.findIndex(x => CIVIC_ONLY.test(x))
  if (civIdx >= 0 && !CIVIC_END.test(street)) street += ' ' + rest.splice(civIdx, 1)[0]
  let comune = rest.find(x => /[a-zà-ú]/i.test(x) && !CIVIC_ONLY.test(x)) ?? ''

  // Stile carta d'identità: "VIA ROMA 12 MILANO (MI)" in un unico segmento.
  if (!comune && foundPlace) {
    const m = /^(.*\d+\s*[a-z]?(?:\/[a-z0-9]+)?)\s+([^\d]+)$/i.exec(street)
    if (m && STREET.test(m[1])) { street = clean(m[1]); comune = clean(m[2]) }
  }
  if (!comune && foundPlace && !STREET.test(street) && !/\d/.test(street)) { comune = street; street = '' }

  Object.assign(out, splitCivic(street), { comune })
  return out
}
